import { useEffect, useState } from "react";
import { Alert, Badge, Button, Form, Table } from "react-bootstrap";
import { FilterSelect, money } from "../components/reports/reportShared.jsx";
import {
  getWorkGivenReport, listEntityRows, isAbortError, errorMessage,
} from "../services/apiClient.js";
import { formatDate } from "../utils/date.js";

// CONTRACTOR WORK — what we have GIVEN each contractor against what they have
// BILLED us, over a period. New in 2.0; 1.0 has no equivalent.
//
// Prompted by the Loop demo (2026-09-01), where this was the vendor's own pitch:
// see how much work you have given somebody over 12 months so you can have the
// rates conversation. GPS could already answer it — nothing was reading the data.
// docs/loop-demo-2026-09-01.md has the timestamps.
//
// Two things about the numbers that the page says out loud rather than assuming
// everyone knows (see services/predefinedReports.js for the full reasoning):
//   • GIVEN is the CONTRACT, not the quote's allowance. It is what we actually
//     committed to the trade, and it is what the overruns report measures too.
//   • The period is the CLAIM'S ACCEPTED DATE, because order dates did not
//     survive the 1.0 import — every order carries a 2026 created_at.

const MODES = [
  ["ytd", "This financial year"],
  ["last_12m", "Last 12 months"],
  ["last_fy", "Last financial year"],
  ["all", "All time"],
];

// Under is the NORMAL state, not a win: work is given long before it is fully
// billed, so most live rows sit negative. Only OVER is worth a colour.
function VarianceCell({ value }) {
  const v = Number(value || 0);
  if (v > 0) return <Badge bg="danger">{money(v)} over</Badge>;
  if (v === 0) return <span className="text-muted">-</span>;
  return <span className="text-muted">{money(v)}</span>;
}

function WorkGivenReportPage() {
  const [mode, setMode] = useState("ytd");
  const [workCentreId, setWorkCentreId] = useState("");
  const [workCentres, setWorkCentres] = useState([]);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    listEntityRows("work-centres").then(setWorkCentres).catch(() => {});
  }, []);

  useEffect(() => {
    // Aborted on unmount and on every dep change (CLAUDE.md / architecture §11).
    const controller = new AbortController();
    setError("");
    setReport(null);
    getWorkGivenReport({
      mode,
      ...(workCentreId ? { work_centre_id: workCentreId } : {}),
      signal: controller.signal,
    })
      .then(setReport)
      .catch((err) => {
        if (isAbortError(err)) return;
        setError(errorMessage(err, "Unable to load the contractor work report."));
      });
    return () => controller.abort();
  }, [mode, workCentreId]);

  function exportCsv() {
    const head = ["Contractor", "Work centres", "Jobs", "Orders", "Given", "Invoiced", "Over/under", "Avg per year", "PL expiry", "Rating"];
    const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const body = report.rows.map((r) =>
      [r.name, r.work_centres || "", r.jobs, r.orders, r.given, r.invoiced, r.variance, r.avg_per_year, r.pl_expiry || "", r.rating ?? ""]
        .map(cell).join(",")
    );
    const blob = new Blob([[head.map(cell).join(","), ...body].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `work-given-${mode}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const t = report?.totals;

  return (
    <div className="page-panel">
      <div className="page-heading"><h2>Work Given to Contractors</h2></div>

      <div className="d-flex flex-wrap align-items-end gap-3 mb-3 no-print">
        <FilterSelect label="Period" value={mode} onChange={setMode} width={200}>
          {MODES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
        </FilterSelect>
        <FilterSelect
          label="Work Centre" value={workCentreId} onChange={setWorkCentreId}
          options={workCentres} width={200}
        />
        <Button variant="outline-secondary" size="sm" onClick={exportCsv} disabled={!report?.rows?.length}>
          Export CSV
        </Button>
      </div>

      <p className="small text-muted" style={{ maxWidth: "80ch" }}>
        <strong>Given</strong> is what we contracted the trade for, not the quote's allowance.{" "}
        <strong>Invoiced</strong> is what they billed, net of anything written off as ignored.
        A contractor showing <em>under</em> usually just has work in progress that is not fully
        billed yet; only <strong>over</strong> is flagged. Jobs are counted by the date the claim
        was accepted, because order dates did not carry across from the old system.
      </p>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {!report && !error ? <p className="text-muted">Loading…</p> : null}

      {report ? (
        <Table striped bordered hover responsive size="sm" className="align-middle">
          <thead className="table-surface">
            <tr>
              <th>Contractor</th>
              <th>Work Centres</th>
              <th className="text-end">Jobs</th>
              <th className="text-end">Orders</th>
              <th className="text-end">Given</th>
              <th className="text-end">Invoiced</th>
              <th className="text-end">Over / Under</th>
              <th className="text-end" data-hint="Total ever given, divided by the years we have used them">Avg / Year</th>
              <th className="text-nowrap">PL Expiry</th>
              <th className="text-end">Rating</th>
            </tr>
          </thead>
          <tbody style={{ fontVariantNumeric: "tabular-nums" }}>
            {report.rows.map((r) => (
              <tr key={r.id}>
                <td>
                  {r.name}
                  {r.is_active ? null : <Badge bg="secondary" className="ms-2">Inactive</Badge>}
                  {r.is_bookkeeping ? <Badge bg="light" text="dark" className="ms-2 border" data-hint="A bookkeeping heading, not a trade">Bookkeeping</Badge> : null}
                </td>
                <td className="small text-muted">{r.work_centres || "-"}</td>
                <td className="text-end">{r.jobs}</td>
                <td className="text-end">{r.orders}</td>
                <td className="text-end">{money(r.given)}</td>
                <td className="text-end">{money(r.invoiced)}</td>
                <td className="text-end"><VarianceCell value={r.variance} /></td>
                <td className="text-end text-muted">{money(r.avg_per_year)}</td>
                <td className="text-nowrap">
                  {r.pl_expiry
                    ? <span className={r.pl_lapsed ? "text-danger fw-semibold" : undefined}>{formatDate(r.pl_expiry)}</span>
                    : <span className="text-danger fw-semibold">None</span>}
                </td>
                <td className="text-end">{r.rating ?? "-"}</td>
              </tr>
            ))}
            {report.rows.length === 0 ? (
              <tr><td colSpan={10} className="text-center text-muted">No contractor work in this period.</td></tr>
            ) : null}
          </tbody>
          {t && report.rows.length > 0 ? (
            <tfoot>
              <tr className="table-surface fw-semibold" style={{ fontVariantNumeric: "tabular-nums" }}>
                <td>{t.contractors} contractors</td>
                <td />
                <td className="text-end">{t.jobs}</td>
                <td className="text-end">{t.orders}</td>
                <td className="text-end">{money(t.given)}</td>
                <td className="text-end">{money(t.invoiced)}</td>
                <td className="text-end">{money(t.variance)}</td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          ) : null}
        </Table>
      ) : null}
    </div>
  );
}

export default WorkGivenReportPage;
